import { Mic, Calendar, Brain, Mail, Puzzle, Target, BarChart3, Users, Monitor } from 'lucide-react'
import Section, { SectionHeader } from '../components/Section'
import Button from '../components/Button'
import { FeatureCard, TestimonialCard } from '../components/Card'

export default function Features() {
    return (
        <>
            {/* Hero Section */}
            <Section className="pt-12 lg:pt-20">
                <div className="text-center max-w-4xl mx-auto">
                    <h1 className="text-4xl lg:text-5xl font-bold text-primary-color leading-tight mb-6">
                        The Complete AI Chief of Staff System
                    </h1>
                    <p className="text-lg lg:text-xl font-normal text-secondary-color leading-relaxed">
                        Three core capabilities that transform how you capture, organize, and execute your work.
                    </p>
                </div>
            </Section>

            {/* Core Features Overview */}
            <Section glass>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <FeatureCard
                        icon={<Mic className="w-full h-full" />}
                        title="Omni-Voice Capture"
                        description="Capture anything, anywhere, instantly"
                    />
                    <FeatureCard
                        icon={<Calendar className="w-full h-full" />}
                        title="Agentic Auto-Scheduler"
                        description="Dynamic time management that adapts to reality"
                    />
                    <FeatureCard
                        icon={<Brain className="w-full h-full" />}
                        title="Chief of Staff Memory"
                        description="Contextual intelligence that remembers everything"
                    />
                </div>
            </Section>

            {/* Feature Spotlight 1: Voice Capture */}
            <Section>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    <div className="glass-prominent p-8 rounded-2xl flex items-center justify-center min-h-[400px]">
                        <div className="text-center">
                            <div className="w-24 h-24 mx-auto mb-6 rounded-full gradient-primary flex items-center justify-center">
                                <Mic className="w-12 h-12 text-white/85" />
                            </div>
                            <p className="text-secondary-color">Real-time voice capture with instant transcription</p>
                        </div>
                    </div>
                    <div className="flex flex-col gap-6">
                        <h2 className="text-3xl font-semibold text-primary-color">Universal Capture Engine</h2>
                        <ul className="flex flex-col gap-4">
                            {[
                                "Real-time transcription under 800ms latency",
                                "Intent extraction: Differentiates tasks, events, notes, and journal entries",
                                "Entity permanence: Knows 'John' is your CFO from past context",
                                "Multi-modal input: Voice, text, or email forwarding"
                            ].map((item, index) => (
                                <li key={index} className="flex items-start gap-3">
                                    <div className="w-2 h-2 rounded-full bg-primary-base mt-2"></div>
                                    <span className="text-secondary-color">{item}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="glass p-6 rounded-xl mt-4">
                            <p className="text-sm font-semibold text-primary-color mb-2">Example Input:</p>
                            <p className="text-sm italic text-secondary-color mb-4">
                                "Remind me to call John on Tuesday about the Q3 report, and I'm feeling stressed about the marketing launch."
                            </p>
                            <p className="text-sm font-semibold text-primary-color mb-2">System Output:</p>
                            <div className="flex flex-col gap-1">
                                <span className="text-sm text-success">✓ Task: Call John re: Q3 Report (Due: Tuesday)</span>
                                <span className="text-sm text-accent-cyan">✓ Journal: Stress regarding Marketing Launch</span>
                            </div>
                        </div>
                    </div>
                </div>
            </Section>

            {/* Feature Spotlight 2: Auto-Scheduler */}
            <Section glass>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    <div className="flex flex-col gap-6 order-2 lg:order-1">
                        <h2 className="text-3xl font-semibold text-primary-color">Intelligent Time Management</h2>
                        <ul className="flex flex-col gap-4">
                            {[
                                "Soft-booking: Tasks tentatively placed based on priority and energy",
                                "Reality check: Auto-adjusts when meetings run over",
                                "Liquid time: Compress your day with AI suggestions",
                                "Contextual guardrails: Learns from your behavior patterns"
                            ].map((item, index) => (
                                <li key={index} className="flex items-start gap-3">
                                    <div className="w-2 h-2 rounded-full bg-primary-base mt-2"></div>
                                    <span className="text-secondary-color">{item}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="glass p-6 rounded-xl mt-4">
                            <p className="text-sm font-semibold text-primary-color mb-2">Scenario:</p>
                            <p className="text-sm text-secondary-color mb-4">"Meeting ran 30 minutes over"</p>
                            <p className="text-sm font-semibold text-primary-color mb-2">AI Response:</p>
                            <p className="text-sm text-accent-cyan">
                                "Your 2 PM deadline shifted. Move to tomorrow morning or push back evening workout?"
                            </p>
                        </div>
                    </div>
                    <div className="glass-prominent p-8 rounded-2xl flex items-center justify-center min-h-[400px] order-1 lg:order-2">
                        <div className="text-center">
                            <div className="w-24 h-24 mx-auto mb-6 rounded-full gradient-primary flex items-center justify-center">
                                <Calendar className="w-12 h-12 text-white/85" />
                            </div>
                            <p className="text-secondary-color">Dynamic scheduling with AI negotiation</p>
                        </div>
                    </div>
                </div>
            </Section>

            {/* Feature Spotlight 3: Memory */}
            <Section>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
                    <div className="glass-prominent p-8 rounded-2xl flex items-center justify-center min-h-[400px]">
                        <div className="text-center">
                            <div className="w-24 h-24 mx-auto mb-6 rounded-full gradient-primary flex items-center justify-center">
                                <Brain className="w-12 h-12 text-white/85" />
                            </div>
                            <p className="text-secondary-color">Long-term contextual memory with instant recall</p>
                        </div>
                    </div>
                    <div className="flex flex-col gap-6">
                        <h2 className="text-3xl font-semibold text-primary-color">Long-Term Contextual Intelligence</h2>
                        <ul className="flex flex-col gap-4">
                            {[
                                "Vector database (RAG): All interactions embedded for instant recall",
                                "Relationship mapping: Remembers people, projects, and commitments",
                                "Smart synthesis: Drafts emails based on your conversation history",
                                "Strategic advice: Provides insights based on patterns"
                            ].map((item, index) => (
                                <li key={index} className="flex items-start gap-3">
                                    <div className="w-2 h-2 rounded-full bg-primary-base mt-2"></div>
                                    <span className="text-secondary-color">{item}</span>
                                </li>
                            ))}
                        </ul>
                        <div className="glass p-6 rounded-xl mt-4">
                            <p className="text-sm font-semibold text-primary-color mb-2">User Query:</p>
                            <p className="text-sm text-secondary-color mb-4">"What did I promise the client last week?"</p>
                            <p className="text-sm font-semibold text-primary-color mb-2">AI Response:</p>
                            <p className="text-sm text-accent-cyan">
                                "You promised a draft by Friday and a budget review. Both are on track."
                            </p>
                        </div>
                    </div>
                </div>
            </Section>

            {/* Secondary Features Grid */}
            <Section glass>
                <SectionHeader title="Everything You Need to Stay in Flow" />
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    <FeatureCard
                        icon={<Mail className="w-full h-full" />}
                        title="Universal Inbox Zero"
                        description="AI triage for Gmail, Slack, and Notion. Auto-bundles newsletters, highlights urgent items."
                    />
                    <FeatureCard
                        icon={<Puzzle className="w-full h-full" />}
                        title="Deep Integrations"
                        description="Two-way sync with Google Calendar, Outlook, Linear, Jira, Trello, and Asana."
                    />
                    <FeatureCard
                        icon={<Target className="w-full h-full" />}
                        title="Focus Mode"
                        description="Show only current task to eliminate overwhelm. Perfect for neurodivergent users."
                    />
                    <FeatureCard
                        icon={<BarChart3 className="w-full h-full" />}
                        title="Smart Analytics"
                        description="Understand your productivity patterns. See where time actually goes."
                    />
                    <FeatureCard
                        icon={<Users className="w-full h-full" />}
                        title="Team Collaboration"
                        description="Shared project spaces, resource planning, and delegation tools."
                    />
                    <FeatureCard
                        icon={<Monitor className="w-full h-full" />}
                        title="Cross-Platform"
                        description="iOS, Android, Mac, Windows, and Web. Sync across all devices instantly."
                    />
                </div>
            </Section>

            {/* Use Cases */}
            <Section>
                <SectionHeader title="Built for Your Workflow" />
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <TestimonialCard
                        quote="It's like having a $100k executive assistant for $29/month."
                        name="The Overwhelmed Founder"
                        role="Juggling sales, product, and operations"
                    />
                    <TestimonialCard
                        quote="First tool that doesn't make me feel broken."
                        name="The Neurodivergent Creator"
                        role="Body double AI that talks through the day"
                    />
                    <TestimonialCard
                        quote="Nothing falls through the cracks anymore."
                        name="The High-Velocity Manager"
                        role="Meeting recording + auto-extraction of follow-ups"
                    />
                </div>
            </Section>

            {/* CTA Section */}
            <Section glass>
                <div className="max-w-4xl mx-auto text-center">
                    <div className="glass-prominent p-12 lg:p-16 rounded-2xl">
                        <h2 className="text-3xl lg:text-4xl font-semibold text-primary-color mb-6">
                            See All Features in Action
                        </h2>
                        <p className="text-lg text-secondary-color mb-8">
                            Start your 14-day trial and experience the full AI Chief of Staff system.
                        </p>
                        <div className="flex flex-wrap justify-center gap-4">
                            <Button to="/signup" size="lg">
                                Start Free Trial
                            </Button>
                            <Button variant="secondary" size="lg">
                                Watch Demo Video
                            </Button>
                        </div>
                    </div>
                </div>
            </Section>
        </>
    )
}
